import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { SearchFacet, SearchQuery, SearchEntityType } from '../../types/search';
import { searchService } from '../../services/searchService';
import { useTaxonomyColors } from '../../hooks/useTaxonomyColors';
import { ENTITY_TONE, entityKindOf, Swatch } from '../ui';

interface FacetedSearchSidebarProps {
  facets: SearchFacet[];
  currentQuery: SearchQuery;
  onFilterChange: (query: SearchQuery) => void;
}

const MAX_VISIBLE = 8;

const FacetSection: React.FC<{
  facet: SearchFacet;
  isExpanded: boolean;
  displayName: string;
  onToggle: () => void;
  onClickValue: (value: string) => void;
  isSelected: (value: string) => boolean;
  formatValue: (value: string) => string;
  /** Colour dot before a value (entity hue or taxonomy colour); null when the value has none */
  markerOf?: (value: string) => React.ReactNode;
}> = ({ facet, isExpanded, displayName, onToggle, onClickValue, isSelected, formatValue, markerOf }) => {
  const [showAll, setShowAll] = useState(false);

  const sortedValues = [...facet.values].sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    return a.value.localeCompare(b.value);
  });
  const visibleValues = showAll ? sortedValues : sortedValues.slice(0, MAX_VISIBLE);
  const hiddenCount = sortedValues.length - MAX_VISIBLE;
  // Keep labels aligned when only some values carry a colour
  const hasMarkers = !!markerOf && facet.values.some((v) => !!markerOf(v.value));

  return (
    <div className="py-2">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        className="flex h-8 w-full items-center justify-between rounded-lg px-2 text-left text-[12px] text-neutral-400 transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
      >
        {displayName}
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform duration-200 ${isExpanded ? '' : '-rotate-90'}`}
          strokeWidth={1.75}
        />
      </button>

      {isExpanded && (
        <ul className="mt-0.5 space-y-px">
          {visibleValues.map((facetValue) => {
            const selected = isSelected(facetValue.value);
            return (
              <li key={facetValue.value}>
                <button
                  type="button"
                  onClick={() => onClickValue(facetValue.value)}
                  aria-pressed={selected}
                  className={`flex h-10 w-full items-center justify-between gap-3 rounded-lg px-2 text-left text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 sm:h-8 ${
                    selected ? 'bg-primary-50 text-primary-700' : 'text-neutral-600 hover:bg-neutral-100/70 hover:text-ink'
                  }`}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    {hasMarkers && (markerOf?.(facetValue.value) || <span className="w-2 flex-shrink-0" aria-hidden="true" />)}
                    <span className="truncate">{formatValue(facetValue.value)}</span>
                  </span>
                  <span className={`text-[12px] tabular-nums ${selected ? 'text-primary-600' : 'text-neutral-400'}`}>
                    {facetValue.count}
                  </span>
                </button>
              </li>
            );
          })}

          {hiddenCount > 0 && (
            <li>
              <button
                type="button"
                onClick={() => setShowAll(!showAll)}
                className="flex h-8 w-full items-center rounded-lg px-2 text-left text-[12px] text-neutral-500 transition-colors hover:text-ink"
              >
                {showAll ? 'Show less' : `Show ${hiddenCount} more`}
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export const FacetedSearchSidebar: React.FC<FacetedSearchSidebarProps> = ({
  facets,
  currentQuery,
  onFilterChange,
}) => {
  const [expandedFacets, setExpandedFacets] = useState<Set<string>>(
    new Set(['entityType', 'organizationType', 'location', 'category'])
  );
  const { colorOf } = useTaxonomyColors();

  const toggleFacet = (facetField: string) => {
    const newExpanded = new Set(expandedFacets);
    if (newExpanded.has(facetField)) {
      newExpanded.delete(facetField);
    } else {
      newExpanded.add(facetField);
    }
    setExpandedFacets(newExpanded);
  };

  const handleFacetValueClick = (facetField: string, value: string) => {
    const updatedQuery = { ...currentQuery };

    switch (facetField) {
      case 'entityType': {
        const currentEntityTypes = currentQuery.entityTypes || Object.values(SearchEntityType);
        const entityType = value as SearchEntityType;
        updatedQuery.entityTypes = currentEntityTypes.includes(entityType)
          ? currentEntityTypes.filter(type => type !== entityType)
          : [...currentEntityTypes, entityType];
        break;
      }
      case 'organizationType':
        updatedQuery.organizationType = currentQuery.organizationType === value ? undefined : value;
        break;
      case 'location':
        updatedQuery.location = currentQuery.location === value ? undefined : value;
        break;
      case 'role':
        updatedQuery.role = currentQuery.role === value ? undefined : value;
        break;
      case 'department':
        updatedQuery.department = currentQuery.department === value ? undefined : value;
        break;
      default:
        break;
    }

    updatedQuery.page = 1;
    onFilterChange(updatedQuery);
  };

  const entityTypesNarrowed =
    !!currentQuery.entityTypes && currentQuery.entityTypes.length !== Object.values(SearchEntityType).length;

  const isValueSelected = (facetField: string, value: string): boolean => {
    switch (facetField) {
      case 'entityType':
        // With every type included nothing is filtered, so nothing reads as selected
        return entityTypesNarrowed && (currentQuery.entityTypes || []).includes(value as SearchEntityType);
      case 'organizationType':
        return currentQuery.organizationType === value;
      case 'location':
        return currentQuery.location === value;
      case 'role':
        return currentQuery.role === value;
      case 'department':
        return currentQuery.department === value;
      default:
        return false;
    }
  };

  const getFacetDisplayName = (facetField: string): string => {
    switch (facetField) {
      case 'entityType': return 'Content Type';
      case 'organizationType': return 'Organization Type';
      case 'location': return 'Location';
      case 'role': return 'Role';
      case 'department': return 'Department';
      case 'category': return 'Category';
      default: return facetField.charAt(0).toUpperCase() + facetField.slice(1);
    }
  };

  const getFacetMarker = (facetField: string, value: string): React.ReactNode => {
    if (facetField === 'entityType') {
      const kind = entityKindOf(value);
      return kind ? <span className={`h-2 w-2 flex-shrink-0 rounded-full ${ENTITY_TONE[kind].solid}`} aria-hidden="true" /> : null;
    }
    const domain =
      facetField === 'organizationType' ? 'organization_type' : facetField === 'category' ? 'stakeholder_category' : null;
    const color = domain ? colorOf(domain, value) : undefined;
    return color ? <Swatch color={color} /> : null;
  };

  const formatFacetValue = (facetField: string, value: string): string => {
    if (facetField === 'entityType') {
      return searchService.getEntityTypePluralName(value as SearchEntityType);
    }
    return value;
  };

  const getActiveFiltersCount = (): number => {
    let count = 0;
    if (entityTypesNarrowed) count++;
    if (currentQuery.organizationType) count++;
    if (currentQuery.location) count++;
    if (currentQuery.role) count++;
    if (currentQuery.department) count++;
    return count;
  };

  const clearAllFilters = () => {
    onFilterChange({
      ...currentQuery,
      entityTypes: Object.values(SearchEntityType),
      organizationType: undefined,
      location: undefined,
      role: undefined,
      department: undefined,
      organizationId: undefined,
      stakeholderId: undefined,
      page: 1,
    });
  };

  if (!facets || facets.length === 0) return null;

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <div className="font-ui">
      <div className="flex h-8 items-center justify-between px-2">
        <h3 className="text-[15px] font-normal tracking-[-0.01em] text-ink">Filters</h3>
        {activeFiltersCount > 0 && (
          <button
            type="button"
            onClick={clearAllFilters}
            className="rounded-full px-2 text-[12px] text-neutral-500 transition-colors hover:text-ink"
          >
            Clear ({activeFiltersCount})
          </button>
        )}
      </div>

      <div className="divide-y divide-neutral-200/70">
        {facets.map((facet) => (
          <FacetSection
            key={facet.field}
            facet={facet}
            isExpanded={expandedFacets.has(facet.field)}
            displayName={getFacetDisplayName(facet.field)}
            onToggle={() => toggleFacet(facet.field)}
            onClickValue={(value) => handleFacetValueClick(facet.field, value)}
            isSelected={(value) => isValueSelected(facet.field, value)}
            formatValue={(value) => formatFacetValue(facet.field, value)}
            markerOf={(value) => getFacetMarker(facet.field, value)}
          />
        ))}
      </div>
    </div>
  );
};
